import React, { useEffect, useRef, useState } from "react";
import { navigate } from "gatsby";

const SearchForm = ({ tab_name, sub_tab_name }) => {
  const [source, setSource] = useState("");
  const [destination, setDestination] = useState("");
  const [flightType, setFlightType] = useState("International");
  const [terminal, setTerminal] = useState("All Terminals");

  const handleSubmit = (e) => {
    e.preventDefault(); // prevent page reload if inside form

    const queryParams = new URLSearchParams({
      source,
      destination,
      flightType,
      terminal,
    }).toString();

    navigate(`/live-flight-information?${queryParams}`);
  };

  const [sourceSuggestions, setSourceSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const sourceWrapperRef = useRef(null);
  const destinationWrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sourceWrapperRef.current &&
        !sourceWrapperRef.current.contains(event.target)
      ) {
        setSourceSuggestions([]); // close source dropdown
      }
      if (
        destinationWrapperRef.current &&
        !destinationWrapperRef.current.contains(event.target)
      ) {
        setDestinationSuggestions([]); // close destination dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchSuggestions = async (query, type) => {
    if (!query) {
      type === "source"
        ? setSourceSuggestions([])
        : setDestinationSuggestions([]);
      return;
    }

    try {
      const res = await fetch(
        `/dial-api/flight-info/flight/${
          type === "source" ? "arrival" : "departure"
        }/${flightType === "Domestic" ? "D" : "I"}?keyword=${query}`,
      );
      const data = await res.json();

      // merge all arrays into one
      let combined = [
        ...(data?.airportDescriptions || []),
        ...(data?.flightNumbers || []),
        ...(data?.iataCodes || []),
      ];

      // filter according to what user typed
      const lowerQuery = query.toLowerCase();
      const results = combined.filter((item) =>
        item.toLowerCase().startsWith(lowerQuery),
      );

      if (type === "source") setSourceSuggestions(results);
      else setDestinationSuggestions(results);
    } catch (err) {
      console.error("Error fetching suggestions:", err);
    }
  };

  useEffect(() => {
    if (tab_name?.toLowerCase().includes("arrivals")) {
      setSource("");
      setDestination("Delhi");
    }
    if (tab_name?.toLowerCase().includes("departure")) {
      setDestination("");
      setSource("Delhi");
    }
    if (tab_name?.toLowerCase().includes("transit")) {
      if (sub_tab_name === "departure") {
        setSource("Delhi");
        setDestination("");
      }
      if (sub_tab_name === "arrivals") {
        setSource("");
        setDestination("Delhi");
      }
    }
  }, [tab_name, sub_tab_name]);

  return (
    <div className="bg-white flex flex-col xl:flex-row gap-[1vh] xl-gap-[2vh] rounded-[2vh] ">
      <div className="w-full  xl:border xl:border-black rounded-[1vh] p-[2vh] xl:p-[1vh] flex flex-col xl:flex-row gap-[3vh] xl:gap-0 z-10">
        {/* Source input */}
        <div
          ref={sourceWrapperRef}
          className="relative border border-black xl:border-0 xl:border-r xl:border-gray-300 rounded-[1vh] xl:rounded-none flex-1"
        >
          <input
            type="text"
            placeholder="Search a source / destinations / flight no."
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              fetchSuggestions(e.target.value, "source");
            }}
            className="w-full rounded-[1vh] xl:rounded-none text-[#1A2352] px-[1vw] xl:px-[2vh] py-[3vw] xl:py-[0.5vh] text-[5vw] xl:text-[2.5vh] focus:outline-none"
          />
          {sourceSuggestions.length > 0 && (
            <ul className="absolute left-0 top-full mt-1 bg-white border border-gray-300 shadow-lg max-h-[30vh] overflow-y-auto z-1 w-full">
              {sourceSuggestions.map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => {
                    setSource(item.name || item);
                    setSourceSuggestions([]);
                  }}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                >
                  {item.name || item}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Destination input */}
        <div
          ref={destinationWrapperRef}
          className="relative border border-black xl:border-0 xl:border-r xl:border-gray-300 rounded-[1vh] xl:rounded-none flex-1"
        >
          <input
            type="text"
            placeholder="Search a source / destinations / flight no."
            value={destination}
            onChange={(e) => {
              setDestination(e.target.value);
              fetchSuggestions(e.target.value, "destination");
            }}
            className="w-full rounded-[1vh] xl:rounded-none text-[#1A2352] px-[1vw] xl:px-[2vh] py-[3vw] xl:py-[0.5vh] text-[5vw] xl:text-[2.5vh] focus:outline-none"
          />
          {destinationSuggestions.length > 0 && (
            <ul className="absolute left-0 top-full mt-1 bg-white border border-gray-300 shadow-lg max-h-[30vh] overflow-y-auto z-1 w-full">
              {destinationSuggestions.map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => {
                    setDestination(item.name || item);
                    setDestinationSuggestions([]);
                  }}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                >
                  {item.name || item}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-black xl:border-0 xl:border-r xl:border-gray-300 rounded-[1vh] xl:rounded-none flex-1">
          {/* Accessible Label for Flight Type */}
          <label htmlFor="flightTypeSelect" className="sr-only">
            Select Flight Type
          </label>

          <select
            id="flightTypeSelect"
            aria-label="Flight Type"
            value={flightType}
            onChange={(e) => setFlightType(e.target.value)}
            className="w-full rounded-[1vh] xl:rounded-none text-[#1A2352] px-[1vw] xl:px-[2vh] py-[3vw] xl:py-[0.5vh] text-[5vw] xl:text-[2.5vh] bg-white focus:outline focus:outline-2 focus:outline-blue-600 focus:outline-offset-2"
          >
            <option value="International">International</option>
            <option value="Domestic">Domestic</option>
          </select>
        </div>

        <div className="border border-black xl:border-0 rounded-[1vh] xl:rounded-none flex-1">
          {/* Accessible Label for Terminal Selector */}
          <label htmlFor="terminalSelect" className="sr-only">
            Select Terminal
          </label>

          <select
            id="terminalSelect"
            aria-label="Terminal Selection"
            value={terminal}
            onChange={(e) => setTerminal(e.target.value)}
            className="w-full rounded-[1vh] xl:rounded-none text-[#1A2352] px-[1vw] xl:px-[2vh] py-[3vw] xl:py-[0.5vh] text-[5vw] xl:text-[2.5vh] bg-white focus:outline focus:outline-2 focus:outline-blue-600 focus:outline-offset-2"
          >
            <option value="All Terminals">All Terminals</option>
            {flightType === "Domestic" && (
              <option value="T1">Terminal 1</option>
            )}
            {flightType === "Domestic" && (
              <option value="T2">Terminal 2</option>
            )}
            <option value="T3">Terminal 3</option>
          </select>
        </div>
      </div>

      <div className="flex px-[3vw] xl:px-0">
        <button
          aria-label="search-button"
          onClick={handleSubmit}
          className="w-full flex items-center justify-center bg-[#1A2352] text-white px-[3vw] xl:px-[2vh] py-[3vw] xl:py-[2vh] rounded-[1vh] text-[5vw] hover:bg-[#101a3a] transition-all"
        >
          <span className="hidden xl:inline-block">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 19.9 19.7"
              width="3vh"
              height="3vh"
            >
              <g fill="none" stroke="currentColor">
                <path strokeLinecap="square" d="M18.5 18.3l-5.4-5.4" />
                <circle cx="8" cy="8" r="7" />
              </g>
            </svg>
          </span>
          <span className="xl:hidden">Search</span>
        </button>
      </div>
    </div>
  );
};

export default SearchForm;
